import { supabase } from '@/lib/supabase';
import type {
  CalendarDensity,
  ConflictCode,
  DayAnnotation,
  Location,
  Schedule,
  Shift,
  TimeFormat,
} from '@/lib/types';

/**
 * Scheduling API helpers (Day 5).
 *
 * All mutations through SECURITY DEFINER RPCs:
 *   schedule_ensure / schedule_publish / schedule_close
 *   shift_upsert / shift_cancel / shift_assign
 *   day_annotation_upsert / day_annotation_delete
 *
 * The shift_upsert RPC throws on conflicts unless override_reason is set.
 * The conflict codes come back in the error message; we parse them so the
 * UI can render a conflict-resolution modal with a required reason field.
 */

// ----- schedules ------------------------------------------------------------

export async function listSchedulesForLocation(
  locationId: string
): Promise<Schedule[]> {
  const { data, error } = await supabase
    .from('schedules')
    .select('*')
    .eq('location_id', locationId)
    .order('month_start_date', { ascending: false });
  if (error) throw error;
  return (data ?? []) as Schedule[];
}

export async function getSchedule(id: string): Promise<Schedule | null> {
  const { data, error } = await supabase
    .from('schedules')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return (data ?? null) as Schedule | null;
}

export async function ensureSchedule(
  locationId: string,
  monthStartDate: string
): Promise<Schedule> {
  const { data, error } = await supabase.rpc('schedule_ensure', {
    p_location_id: locationId,
    p_month_start: monthStartDate,
  });
  if (error) throw error;
  return data as Schedule;
}

export async function publishSchedule(scheduleId: string): Promise<Schedule> {
  const { data, error } = await supabase.rpc('schedule_publish', {
    p_schedule_id: scheduleId,
  });
  if (error) throw error;
  return data as Schedule;
}

export async function closeSchedule(scheduleId: string): Promise<Schedule> {
  const { data, error } = await supabase.rpc('schedule_close', {
    p_schedule_id: scheduleId,
  });
  if (error) throw error;
  return data as Schedule;
}

// ----- shifts ---------------------------------------------------------------

export async function listShiftsForSchedule(scheduleId: string): Promise<Shift[]> {
  const { data, error } = await supabase
    .from('shifts')
    .select('*')
    .eq('schedule_id', scheduleId)
    .order('shift_date', { ascending: true })
    .order('start_time', { ascending: true });
  if (error) throw error;
  return (data ?? []) as Shift[];
}

export interface ShiftUpsertPayload {
  id?: string | null;
  location_id: string;
  shift_date: string;
  start_time: string;
  end_time: string;
  break_minutes?: number;
  staff_id?: string | null;
  is_open?: boolean;
  notes?: string | null;
  override_reason?: string | null;
}

export interface ShiftConflictError {
  conflicts: ConflictCode[];
  message: string;
}

/** Throws an Error whose `.cause` is `{ conflicts: ConflictCode[] }` on conflict. */
export async function upsertShift(payload: ShiftUpsertPayload): Promise<Shift> {
  const { data, error } = await supabase.rpc('shift_upsert', {
    p_id: payload.id ?? null,
    p_location_id: payload.location_id,
    p_shift_date: payload.shift_date,
    p_start_time: payload.start_time,
    p_end_time: payload.end_time,
    p_break_minutes: payload.break_minutes ?? 0,
    p_staff_id: payload.staff_id ?? null,
    p_is_open: payload.is_open ?? false,
    p_notes: payload.notes ?? null,
    p_override_reason: payload.override_reason ?? null,
  });
  if (error) {
    const conflicts = parseConflictCodes(error.message);
    if (conflicts.length > 0) {
      const wrapped: Error & { conflicts?: ConflictCode[] } = new Error(
        `Shift has conflicts: ${conflicts.join(', ')}`
      );
      wrapped.conflicts = conflicts;
      throw wrapped;
    }
    throw error;
  }
  return data as Shift;
}

export async function cancelShift(id: string, reason: string): Promise<Shift> {
  const { data, error } = await supabase.rpc('shift_cancel', {
    p_id: id,
    p_reason: reason,
  });
  if (error) throw error;
  return data as Shift;
}

export async function assignShift(
  id: string,
  staffId: string | null,
  overrideReason: string | null = null
): Promise<Shift> {
  const { data, error } = await supabase.rpc('shift_assign', {
    p_id: id,
    p_staff_id: staffId,
    p_override_reason: overrideReason,
  });
  if (error) {
    const conflicts = parseConflictCodes(error.message);
    if (conflicts.length > 0) {
      const wrapped: Error & { conflicts?: ConflictCode[] } = new Error(
        `Shift has conflicts: ${conflicts.join(', ')}`
      );
      wrapped.conflicts = conflicts;
      throw wrapped;
    }
    throw error;
  }
  return data as Shift;
}

// ----- day annotations ------------------------------------------------------

export async function listDayAnnotations(
  locationId: string,
  monthStartDate: string
): Promise<DayAnnotation[]> {
  // Compute the next-month-start without going through `new Date(YYYY-MM-DD)`,
  // which parses as UTC and shifts in negative-offset zones (the same class
  // of bug fixed in Day 5 for the shift list).
  const [y, m] = monthStartDate.split('-').map(Number);
  const ny = m === 12 ? y + 1 : y;
  const nm = m === 12 ? 1 : m + 1;
  const end = `${ny}-${String(nm).padStart(2, '0')}-01`;
  const { data, error } = await supabase
    .from('day_annotations')
    .select('*')
    .eq('location_id', locationId)
    .gte('annotation_date', monthStartDate)
    .lt('annotation_date', end)
    .order('annotation_date', { ascending: true });
  if (error) throw error;
  return (data ?? []) as DayAnnotation[];
}

export async function upsertDayAnnotation(
  locationId: string,
  annotationDate: string,
  note: string
): Promise<DayAnnotation> {
  const { data, error } = await supabase.rpc('day_annotation_upsert', {
    p_location_id: locationId,
    p_annotation_date: annotationDate,
    p_note: note,
  });
  if (error) throw error;
  return data as DayAnnotation;
}

export async function deleteDayAnnotation(
  locationId: string,
  annotationDate: string
): Promise<void> {
  const { error } = await supabase.rpc('day_annotation_delete', {
    p_location_id: locationId,
    p_annotation_date: annotationDate,
  });
  if (error) throw error;
}

/** Day 6.5: upsert with optional tags array. Pass empty tags + empty note to delete. */
export async function upsertDayAnnotationWithTags(
  locationId: string,
  annotationDate: string,
  note: string | null,
  tags: string[]
): Promise<DayAnnotation | null> {
  const { data, error } = await supabase.rpc('day_annotation_upsert', {
    p_location_id: locationId,
    p_annotation_date: annotationDate,
    p_note: note,
    p_tags: tags,
  });
  if (error) throw error;
  return (data ?? null) as DayAnnotation | null;
}

// ----- Day 6.5 calendar RPCs ------------------------------------------------

export async function cloneSchedule(
  sourceScheduleId: string,
  targetMonthStart: string
): Promise<Schedule> {
  const { data, error } = await supabase.rpc('schedule_clone', {
    p_source_schedule_id: sourceScheduleId,
    p_target_month_start: targetMonthStart,
  });
  if (error) throw error;
  return data as Schedule;
}

export async function updateScheduleNotes(
  scheduleId: string,
  notes: string
): Promise<Schedule> {
  const { data, error } = await supabase.rpc('schedule_notes_update', {
    p_schedule_id: scheduleId,
    p_notes: notes,
  });
  if (error) throw error;
  return data as Schedule;
}

// ----- copy day (0107 + 0108) -----------------------------------------------

export type CopyDaySkipReason = 'approved_time_off' | 'staff_inactive_on_date';

export interface CopyDaySkipped {
  date: string;              // ISO YYYY-MM-DD
  staff_name: string;
  reason: CopyDaySkipReason;
  // Present only when reason === 'approved_time_off'. Raw enum value from
  // time_off_requests.type (vacation | sick | personal | unpaid | other).
  time_off_type?: string;
}

export interface CopyDayResult {
  ok: boolean;
  copied_count?: number;
  skipped?: CopyDaySkipped[];
  target_count?: number;
  override_conflicts?: boolean;
  // Present only when ok:false
  error?: string;
  conflict_dates?: string[];
}

/**
 * Copy every shift from one day of a draft schedule onto 1..N other days
 * of the SAME draft schedule (same month, same location).
 *
 * Backend (schedule_copy_day, 0107 + 0108) enforces:
 *   - Draft status only
 *   - Target dates must be inside the schedule's month
 *   - Refuses the whole op if any target already has shifts (returns
 *     ok:false + conflict_dates so the UI can point the user at what to
 *     clear first)
 *   - Per-shift skips reported in skipped[]:
 *     * approved_time_off (payload includes time_off_type)
 *     * staff_inactive_on_date
 *   - overrideConflicts=true bypasses BOTH per-shift skips and stamps
 *     override_reason='copy-day override' on the resulting shifts. The
 *     structural refuses above still apply.
 */
export async function copyDayShifts(
  locationId: string,
  sourceDate: string,
  targetDates: string[],
  overrideConflicts = false
): Promise<CopyDayResult> {
  const { data, error } = await supabase.rpc('schedule_copy_day', {
    p_location_id: locationId,
    p_source_date: sourceDate,
    p_target_dates: targetDates,
    p_override_conflicts: overrideConflicts,
  });
  if (error) throw error;
  return data as CopyDayResult;
}

export async function updateLocationTags(
  locationId: string,
  tags: string[]
): Promise<Location> {
  const { data, error } = await supabase.rpc('location_tags_update', {
    p_location_id: locationId,
    p_tags: tags,
  });
  if (error) throw error;
  return data as Location;
}

export async function updateLocationCalendarPrefs(
  locationId: string,
  density: CalendarDensity | null,
  timeFormat: TimeFormat | null
): Promise<Location> {
  const { data, error } = await supabase.rpc('location_calendar_prefs_update', {
    p_location_id: locationId,
    p_density: density,
    p_time_format: timeFormat,
  });
  if (error) throw error;
  return data as Location;
}

// ----- time format helpers --------------------------------------------------

/**
 * Format a "HH:MM:SS" or "HH:MM" string per the location's preference.
 *   am_pm  -> "9a", "12p", "12:30p"
 *   h24    -> "9", "12", "12:30"
 */
export function formatTimeOfDay(t: string, fmt: TimeFormat): string {
  const [hStr, mStr] = t.split(':');
  const h = Number(hStr);
  const m = Number(mStr ?? '0');
  if (fmt === 'h24') {
    return m === 0 ? `${h}` : `${h}:${String(m).padStart(2, '0')}`;
  }
  let display: number;
  let suffix: 'a' | 'p';
  if (h === 0) { display = 12; suffix = 'a'; }
  else if (h < 12) { display = h; suffix = 'a'; }
  else if (h === 12) { display = 12; suffix = 'p'; }
  else { display = h - 12; suffix = 'p'; }
  return m === 0
    ? `${display}${suffix}`
    : `${display}:${String(m).padStart(2, '0')}${suffix}`;
}

/** Format a shift's time range for display in a chip: "9a-1p". */
export function formatShiftRange(
  startTime: string,
  endTime: string,
  fmt: TimeFormat
): string {
  return `${formatTimeOfDay(startTime, fmt)}-${formatTimeOfDay(endTime, fmt)}`;
}

/**
 * Calendar-chip version: uppercase AM/PM, leading-space pad on single-digit
 * hours so digits visually align when chips are stacked.
 *   am_pm  -> " 9AM-12PM", "10AM- 2PM", "12PM-12AM"
 *   h24    -> " 9:00-12:00"
 * Used by DayCell.ShiftChip together with `tabular-nums` + `whitespace-pre`
 * so the dash position is the same across all chips in a column.
 */
export function formatShiftRangeForChip(
  startTime: string,
  endTime: string,
  fmt: TimeFormat
): string {
  return `${formatTimeForChip(startTime, fmt)}-${formatTimeForChip(endTime, fmt)}`;
}

function formatTimeForChip(t: string, fmt: TimeFormat): string {
  const [hStr, mStr] = t.split(':');
  const h = Number(hStr);
  const m = Number(mStr ?? '0');
  if (fmt === 'h24') {
    const hPad = String(h).padStart(2, ' ');
    return m === 0 ? hPad : `${hPad}:${String(m).padStart(2, '0')}`;
  }
  let display: number;
  let suffix: 'AM' | 'PM';
  if (h === 0) { display = 12; suffix = 'AM'; }
  else if (h < 12) { display = h; suffix = 'AM'; }
  else if (h === 12) { display = 12; suffix = 'PM'; }
  else { display = h - 12; suffix = 'PM'; }
  const hPad = display < 10 ? ` ${display}` : String(display);
  return m === 0
    ? `${hPad}${suffix}`
    : `${hPad}:${String(m).padStart(2, '0')}${suffix}`;
}

/** Two-letter initials from a display name. "Sarah Patel" -> "SP". */
export function staffInitials(displayName: string): string {
  const parts = displayName.trim().split(/\s+/);
  if (parts.length === 0) return '??';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * 0109: Return the display-ready initials for a staff member.
 * Priority: custom_initials -> derived from display_name.
 * Mirrors the Postgres `compute_staff_initials()` helper so the client
 * fallback matches what the trigger stamps into shifts.staff_initials_at_save.
 */
export function displayInitialsForStaff(
  staff: { custom_initials?: string | null; display_name?: string | null }
): string {
  const c = staff.custom_initials?.trim();
  if (c) return c;
  return staffInitials(staff.display_name ?? '');
}

/**
 * 0109: Return the display-ready initials for a shift chip on the calendar.
 * Priority: staff_initials_at_save (frozen snapshot) -> derived from
 * staff_display_name_at_save (fallback for shifts created before 0109).
 */
export function displayInitialsForShift(
  shift: {
    staff_initials_at_save?: string | null;
    staff_display_name_at_save?: string | null;
  }
): string {
  const snap = shift.staff_initials_at_save?.trim();
  if (snap) return snap;
  const disp = shift.staff_display_name_at_save?.trim();
  if (disp) return staffInitials(disp);
  return '--';
}

/**
 * 0109: format a raw initials value for on-chip display. Auto-dots ONLY
 * pure 2-letter values, matching pre-0109 behavior for backwards visual
 * consistency ("SA" -> "S.A."). Any other value (custom-set "SAK",
 * "S.A.K.", "L-C", etc.) is rendered as-typed.
 */
export function formatInitialsForChip(initials: string): string {
  if (initials.length === 2 && /^[A-Z]+$/.test(initials)) {
    return initials[0] + '.' + initials[1] + '.';
  }
  return initials;
}

/**
 * 0109: format constraint for custom_initials.
 * 1-4 chars, must start with A-Z, remainder A-Z/./-.
 * Matches the staff_custom_initials_format CHECK constraint (0109).
 */
export const CUSTOM_INITIALS_REGEX = /^[A-Z][A-Z.-]{0,3}$/;

export function isValidCustomInitials(value: string): boolean {
  return CUSTOM_INITIALS_REGEX.test(value);
}

/** Decide whether a shift renders in the AM or PM half, based on its start_time. */
export function shiftHalf(
  startTime: string,
  shiftSplitTime: string
): 'am' | 'pm' {
  return startTime < shiftSplitTime ? 'am' : 'pm';
}


// ----- helpers restored from Day 5/6 (lost during heredoc rewrite) ---------

const ALL_CODES: ConflictCode[] = [
  'DOUBLE_BOOK',
  'INACTIVE_STAFF',
  'TIME_OFF_OVERLAP',
  'OUT_OF_HOURS',
];

/** Pulls conflict codes out of a postgres error message. */
function parseConflictCodes(message: string): ConflictCode[] {
  const found = new Set<ConflictCode>();
  for (const c of ALL_CODES) {
    if (message.includes(c)) found.add(c);
  }
  return Array.from(found);
}

/** Format HH:MM:SS -> HH:MM for display. */
export function formatTime(t: string): string {
  return t.slice(0, 5);
}

/** Returns YYYY-MM-01 for a given Date. */
export function monthStart(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

export function currentMonthStart(): string {
  return monthStart(new Date());
}

// ----- pharmacist on-duty gaps (SCH-AD-09) ---------------------------------

export interface PharmacistOnDutyGap {
  gap_date: string;
  gap_start: string;
  gap_end: string;
}

export async function listPharmacistOnDutyGaps(
  locationId: string,
  monthStartDate: string
): Promise<PharmacistOnDutyGap[]> {
  const { data, error } = await supabase.rpc('v_pharmacist_on_duty_gaps', {
    p_location_id: locationId,
    p_month_start: monthStartDate,
  });
  if (error) throw error;
  return (data ?? []) as PharmacistOnDutyGap[];
}
